import { z } from 'zod';

export const ENTITY_ID_PREFIXES = {
  agentRun: 'agent_run_',
  artifact: 'artifact_',
  auditEvent: 'audit_',
  chatAttachment: 'chat_attachment_',
  comment: 'comment_',
  commentThread: 'comment_thread_',
  deck: 'deck_',
  deckImport: 'import_',
  file: 'file_',
  folder: 'folder_',
  space: 'space_',
  session: 'session_',
  sheet: 'sheet_',
  slide: 'slide_',
  workbook: 'workbook_',
} as const;

export type EntityIdKind = keyof typeof ENTITY_ID_PREFIXES;
export type EntityId<K extends EntityIdKind> =
  `${(typeof ENTITY_ID_PREFIXES)[K]}${string}` & { readonly __kind: K };

const HEX_ID_LENGTH = 32;

function entityIdPattern(kind: EntityIdKind) {
  return new RegExp(`^${ENTITY_ID_PREFIXES[kind]}[0-9a-f]{${HEX_ID_LENGTH}}$`);
}

export function createEntityId<K extends EntityIdKind>(kind: K): EntityId<K> {
  const uuid = globalThis.crypto.randomUUID() as string & {
    replaceAll(searchValue: string, replaceValue: string): string;
  };
  return `${ENTITY_ID_PREFIXES[kind]}${uuid.replaceAll('-', '')}` as EntityId<K>;
}

export function entityIdSchema<K extends EntityIdKind>(kind: K) {
  return z.string().regex(entityIdPattern(kind));
}

export function isEntityId<K extends EntityIdKind>(
  kind: K,
  value: unknown
): value is EntityId<K> {
  return typeof value === 'string' && entityIdPattern(kind).test(value);
}

export const SLIDE_ID_PREFIX = ENTITY_ID_PREFIXES.slide;
export const SlideIdSchema = entityIdSchema('slide');
export type SlideId = EntityId<'slide'>;

export function createSlideId(): SlideId {
  return createEntityId('slide');
}

export function isSlideId(value: unknown): value is SlideId {
  return isEntityId('slide', value);
}

export const PROJECT_ID_PREFIX = ENTITY_ID_PREFIXES.space;
export const SpaceIdSchema = entityIdSchema('space');
export type SpaceId = EntityId<'space'>;
export const createSpaceId = () => createEntityId('space');
export const isSpaceId = (value: unknown): value is SpaceId =>
  isEntityId('space', value);

export const DECK_ID_PREFIX = ENTITY_ID_PREFIXES.deck;
export const DeckIdSchema = entityIdSchema('deck');
export type DeckId = EntityId<'deck'>;
export const createDeckId = () => createEntityId('deck');
export const isDeckId = (value: unknown): value is DeckId =>
  isEntityId('deck', value);

export const FOLDER_ID_PREFIX = ENTITY_ID_PREFIXES.folder;
export const FolderIdSchema = entityIdSchema('folder');
export type FolderId = EntityId<'folder'>;
export const createFolderId = () => createEntityId('folder');
export const isFolderId = (value: unknown): value is FolderId =>
  isEntityId('folder', value);

export const FILE_ID_PREFIX = ENTITY_ID_PREFIXES.file;
export const FileIdSchema = entityIdSchema('file');
export type FileId = EntityId<'file'>;
export const createFileId = () => createEntityId('file');
export const isFileId = (value: unknown): value is FileId =>
  isEntityId('file', value);

export const SESSION_ID_PREFIX = ENTITY_ID_PREFIXES.session;
export const SessionIdSchema = entityIdSchema('session');
export type SessionId = EntityId<'session'>;
export const createSessionId = () => createEntityId('session');
export const isSessionId = (value: unknown): value is SessionId =>
  isEntityId('session', value);

export const AGENT_RUN_ID_PREFIX = ENTITY_ID_PREFIXES.agentRun;
export const AgentRunIdSchema = entityIdSchema('agentRun');
export type AgentRunId = EntityId<'agentRun'>;
export const createAgentRunId = () => createEntityId('agentRun');
export const isAgentRunId = (value: unknown): value is AgentRunId =>
  isEntityId('agentRun', value);

export const DECK_IMPORT_ID_PREFIX = ENTITY_ID_PREFIXES.deckImport;
export const DeckImportIdSchema = entityIdSchema('deckImport');
export type DeckImportId = EntityId<'deckImport'>;
export const createDeckImportId = () => createEntityId('deckImport');
export const isDeckImportId = (value: unknown): value is DeckImportId =>
  isEntityId('deckImport', value);

export const ARTIFACT_ID_PREFIX = ENTITY_ID_PREFIXES.artifact;
export const ArtifactIdSchema = entityIdSchema('artifact');
export type ArtifactId = EntityId<'artifact'>;
export const createArtifactId = () => createEntityId('artifact');
export const isArtifactId = (value: unknown): value is ArtifactId =>
  isEntityId('artifact', value);

export const COMMENT_THREAD_ID_PREFIX = ENTITY_ID_PREFIXES.commentThread;
export const CommentThreadIdSchema = entityIdSchema('commentThread');
export type CommentThreadId = EntityId<'commentThread'>;
export const createCommentThreadId = () => createEntityId('commentThread');
export const isCommentThreadId = (value: unknown): value is CommentThreadId =>
  isEntityId('commentThread', value);

export const COMMENT_ID_PREFIX = ENTITY_ID_PREFIXES.comment;
export const CommentIdSchema = entityIdSchema('comment');
export type CommentId = EntityId<'comment'>;
export const createCommentId = () => createEntityId('comment');
export const isCommentId = (value: unknown): value is CommentId =>
  isEntityId('comment', value);

export const CHAT_ATTACHMENT_ID_PREFIX = ENTITY_ID_PREFIXES.chatAttachment;
export const ChatAttachmentIdSchema = entityIdSchema('chatAttachment');
export type ChatAttachmentId = EntityId<'chatAttachment'>;
export const createChatAttachmentId = () => createEntityId('chatAttachment');
export const isChatAttachmentId = (value: unknown): value is ChatAttachmentId =>
  isEntityId('chatAttachment', value);

export const WORKBOOK_ID_PREFIX = ENTITY_ID_PREFIXES.workbook;
export const WorkbookIdSchema = entityIdSchema('workbook');
export type WorkbookId = EntityId<'workbook'>;
export const createWorkbookId = () => createEntityId('workbook');
export const isWorkbookId = (value: unknown): value is WorkbookId =>
  isEntityId('workbook', value);

export const SHEET_ID_PREFIX = ENTITY_ID_PREFIXES.sheet;
export const SheetIdSchema = entityIdSchema('sheet');
export type SheetId = EntityId<'sheet'>;
export const createSheetId = () => createEntityId('sheet');
export const isSheetId = (value: unknown): value is SheetId =>
  isEntityId('sheet', value);

/**
 * A polymorphic document reference: decks and workbooks share `documentId`
 * columns and wire fields, discriminated by the id prefix. There is no DB
 * foreign key behind these — validate every entry point through this schema.
 */
export const DocumentIdSchema = z.union([DeckIdSchema, WorkbookIdSchema]);
export type DocumentId = DeckId | WorkbookId;
export type DocumentKind = 'deck' | 'workbook';

export function documentKindFromId(id: string): DocumentKind {
  if (isDeckId(id)) return 'deck';
  if (isWorkbookId(id)) return 'workbook';
  throw new Error(`Not a document id: ${id}`);
}

export function isDocumentId(value: unknown): value is DocumentId {
  return isDeckId(value) || isWorkbookId(value);
}
